import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

const POLICIES_URL = '/secure/policies/intune'

const getPolicyTimelinesUrl = ({ source, policyId }) => `${POLICIES_URL}/${source}/${policyId}/timelines`
const getLatestUpdatesUrl = (source) => `${POLICIES_URL}/${source}/timelines/latest-updates`

export const intunePolicyTimelinesApi = createApi({
  reducerPath: 'intunePolicyTimelines',
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getPolicyTimelines: builder.query({
      query: ({ source, policyId, customerId }) => ({
        url: getPolicyTimelinesUrl({ source, policyId }),
        params: { customerId },
      }),
    }),
    getPolicyTimelineLatestUpdates: builder.query({
      query: ({ source, customerId }) => ({
        url: getLatestUpdatesUrl(source),
        params: { customerId },
      }),
    }),
    // A list can bring several collections together, so the latest change is read for each of them.
    getPolicyTimelineLatestUpdatesBySource: builder.query({
      queryFn: async ({ sources, customerId }, _queryApi, _extraOptions, baseQuery) => {
        const results = await Promise.all(
          sources.map((source) => baseQuery({ url: getLatestUpdatesUrl(source), params: { customerId } })),
        )

        if (results.every((result) => result.error)) {
          return { error: results[0].error }
        }

        return { data: results.flatMap((result) => (Array.isArray(result.data) ? result.data : [])) }
      },
    }),
  }),
})

export const {
  useGetPolicyTimelinesQuery,
  useLazyGetPolicyTimelinesQuery,
  useGetPolicyTimelineLatestUpdatesQuery,
  useLazyGetPolicyTimelineLatestUpdatesQuery,
  useGetPolicyTimelineLatestUpdatesBySourceQuery,
} = intunePolicyTimelinesApi

export const useGetPolicyTimelines = ({ source, policyId }, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetPolicyTimelinesQuery(
    { source, policyId, customerId },
    { refetchOnMountOrArgChange: true, ...options, skip: options.skip || !source || !policyId },
  )
}

export const useGetPolicyTimelineLatestUpdates = (source, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetPolicyTimelineLatestUpdatesQuery({ source, customerId }, { ...options, skip: options.skip || !source })
}

export const useGetPolicyTimelineLatestUpdatesBySource = (sources, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetPolicyTimelineLatestUpdatesBySourceQuery(
    { sources, customerId },
    { refetchOnMountOrArgChange: true, ...options, skip: options.skip || !sources?.length || !customerId },
  )
}
